export type Difficulty = 'easy' | 'medium' | 'hard';

export interface WordData {
  target: string;
  typed: string;
  completed: boolean;
}

/* ─── EASY: short, very common words (2–5 chars) ───────────────────────── */
const WORDS_EASY = [
  'the', 'be', 'to', 'of', 'and', 'in', 'it', 'for', 'not', 'on', 'as',
  'you', 'do', 'at', 'but', 'by', 'we', 'say', 'she', 'or', 'an', 'my',
  'one', 'all', 'so', 'up', 'out', 'if', 'go', 'me', 'can', 'no', 'him',
  'two', 'how', 'our', 'new', 'any', 'day', 'us', 'now', 'see', 'then',
  'back', 'some', 'come', 'just', 'run', 'few', 'next', 'red', 'feel',
  'talk', 'soon', 'far', 'top', 'half', 'fast', 'each', 'best', 'room',
  'kind', 'walk', 'both', 'move', 'step', 'even', 'high', 'left', 'last',
  'late', 'yet', 'off', 'own', 'away', 'wide', 'hard', 'face', 'keep',
  'long', 'made', 'read', 'over', 'help', 'show', 'old', 'try', 'let',
  'put', 'big', 'ask', 'cut', 'age', 'set', 'air', 'eye', 'pay', 'lay',
  'act', 'arm', 'eat', 'sit', 'car', 'end', 'lot', 'way', 'sea', 'job',
  'hot', 'bit', 'dog', 'cat', 'sun', 'fly', 'map', 'cup', 'bed', 'hat',
  'box', 'key', 'ice', 'sky', 'day', 'ago', 'add', 'oil', 'buy', 'war',
  'win', 'hit', 'got', 'did', 'has', 'had', 'its', 'may', 'use', 'get',
  'his', 'her', 'who', 'why', 'men', 'nor', 'too', 'via', 'due', 'per',
  'ten', 'six', 'mix', 'fit', 'fix', 'low', 'raw', 'wet', 'dry', 'log',
];

/* ─── MEDIUM: mixed-length common vocabulary ───────────────────────────── */
const WORDS_MEDIUM = [
  'the', 'be', 'to', 'of', 'and', 'in', 'that', 'have', 'it', 'for',
  'not', 'on', 'with', 'as', 'you', 'do', 'at', 'this', 'but', 'by',
  'from', 'they', 'we', 'say', 'her', 'she', 'or', 'an', 'will', 'my',
  'one', 'all', 'would', 'there', 'their', 'what', 'so', 'up', 'out',
  'if', 'about', 'who', 'get', 'which', 'go', 'me', 'when', 'make',
  'can', 'like', 'time', 'no', 'just', 'him', 'know', 'take', 'people',
  'into', 'year', 'your', 'good', 'some', 'could', 'them', 'see', 'other',
  'than', 'then', 'now', 'look', 'only', 'come', 'its', 'over', 'think',
  'also', 'back', 'after', 'use', 'two', 'how', 'our', 'work', 'first',
  'well', 'way', 'even', 'new', 'want', 'because', 'any', 'these', 'give',
  'day', 'most', 'great', 'between', 'need', 'large', 'often', 'hand',
  'high', 'place', 'turn', 'found', 'still', 'learn', 'food', 'state',
  'never', 'last', 'let', 'city', 'tree', 'farm', 'hard', 'start',
  'might', 'story', 'far', 'sea', 'left', 'late', 'run', 'while',
  'close', 'night', 'real', 'life', 'few', 'north', 'open', 'seem',
  'next', 'white', 'begin', 'walk', 'paper', 'group', 'always', 'music',
  'those', 'both', 'mark', 'book', 'letter', 'until', 'mile', 'river',
  'car', 'feet', 'care', 'second', 'enough', 'girl', 'young', 'ready',
  'above', 'ever', 'red', 'list', 'feel', 'talk', 'bird', 'soon',
  'body', 'dog', 'family', 'leave', 'song', 'door', 'black', 'short',
  'class', 'wind', 'happen', 'ship', 'area', 'half', 'rock', 'order',
  'fire', 'south', 'problem', 'piece', 'since', 'top', 'whole', 'space',
  'best', 'hour', 'better', 'true', 'during', 'five', 'step', 'early',
  'ground', 'reach', 'fast', 'fill', 'heavy', 'simple', 'bring', 'light',
  'voice', 'power', 'town', 'drive', 'fall', 'lead', 'dark', 'note',
  'wait', 'plan', 'star', 'field', 'rest', 'front', 'teach', 'week',
  'final', 'green', 'quick', 'ocean', 'warm', 'free', 'minute', 'strong',
  'mind', 'clear', 'fact', 'street', 'common', 'gold', 'rain', 'answer',
  'plain', 'trade', 'weight', 'floor', 'shape', 'course', 'stand',
  'heart', 'cold', 'deep', 'build', 'lost', 'change', 'watch', 'follow',
  'small', 'clean', 'surface', 'travel', 'morning', 'evening', 'center',
  'store', 'wood', 'meant', 'meant', 'meant', 'watch', 'follow', 'wrote',
];

/* ─── HARD: long complex vocabulary + numbers/symbols injected ─────────── */
const WORDS_HARD = [
  'although', 'beautiful', 'because', 'between', 'business', 'children',
  'complete', 'consider', 'continue', 'describe', 'develop', 'different',
  'difficult', 'evidence', 'exercise', 'experience', 'following', 'function',
  'history', 'important', 'including', 'increase', 'influence', 'involved',
  'language', 'material', 'movement', 'national', 'necessary', 'nothing',
  'original', 'personal', 'physical', 'political', 'position', 'possible',
  'practice', 'probably', 'question', 'required', 'remember', 'solution',
  'something', 'sometimes', 'specific', 'standard', 'strength', 'structure',
  'suggest', 'support', 'technical', 'together', 'thousand', 'without',
  'whatever', 'whether', 'whenever', 'condition', 'education', 'efficient',
  'establish', 'everywhere', 'excellent', 'fantastic', 'government',
  'happening', 'immediate', 'introduce', 'knowledge', 'maintain',
  'management', 'marketing', 'necessary', 'observation', 'opportunity',
  'particular', 'performance', 'phenomenon', 'population', 'president',
  'principal', 'procedure', 'professor', 'recognize', 'reduction',
  'regulation', 'relationship', 'represent', 'responsible', 'restaurant',
  'scientific', 'settlement', 'situation', 'statistics', 'substantial',
  'successful', 'technology', 'transport', 'university', 'vegetable',
  'absolute', 'accomplish', 'according', 'additional', 'advantage',
  'affection', 'afternoon', 'agreement', 'alongside', 'ambitious',
  'atmosphere', 'beginning', 'breakthrough', 'calculation', 'capability',
  'characteristic', 'comfortable', 'commitment', 'communication',
  'competition', 'concentrate', 'consequence', 'considerable', 'consumption',
  'contribution', 'conventional', 'coordinate', 'corporation', 'creativity',
  'declaration', 'demonstrate', 'dependency', 'description', 'destination',
  'determination', 'development', 'distinction', 'distribution', 'diversity',
  'documentation', 'effectiveness', 'elimination', 'engagement', 'enthusiasm',
  'environment', 'establishment', 'evaluation', 'examination', 'expectation',
  'explanation', 'exploitation', 'extraordinary', 'flexibility', 'foundation',
  'furthermore', 'generation', 'geographical', 'globalization', 'graduation',
  'guaranteed', 'identification', 'imagination', 'implementation',
  'improvement', 'independence', 'indication', 'information', 'innovation',
  'inspiration', 'instruction', 'integration', 'interpretation', 'introduction',
  'investigation', 'justification', 'leadership', 'legislation', 'maintenance',
  'manufacturing', 'measurement', 'modification', 'motivation', 'navigation',
  'negotiation', 'normalization', 'notification', 'observation', 'optimization',
  'organization', 'participation', 'partnership', 'perception', 'perspective',
  'phenomenon', 'presentation', 'prioritization', 'probability', 'productivity',
  'professional', 'qualification', 'quantification', 'recognition', 'reflection',
  'rehabilitation', 'reinforcement', 'representation', 'responsibility',
  'simplification', 'specification', 'standardization', 'transformation',
  'understanding', 'visualization', 'vulnerability',
];

const HARD_NUMBERS = ['42', '100', '256', '99', '2048', '3', '16', '404', '64', '1024', '7', '2024'];
const HARD_SYMBOLS = [',', '.', '?', '!', ';'];

/* ─── GENERATOR ─────────────────────────────────────────────────────────── */
export function generateWords(count: number, difficulty: Difficulty): WordData[] {
  const bank = difficulty === 'easy' ? WORDS_EASY : difficulty === 'hard' ? WORDS_HARD : WORDS_MEDIUM;
  const shuffled = [...bank].sort(() => Math.random() - 0.5);
  const result: WordData[] = [];
  let idx = 0;

  while (result.length < count) {
    result.push({ target: shuffled[idx % shuffled.length], typed: '', completed: false });
    idx++;

    if (difficulty === 'hard' && result.length < count - 1) {
      const r = Math.random();
      if (r < 0.08) {
        result.push({
          target: HARD_NUMBERS[Math.floor(Math.random() * HARD_NUMBERS.length)],
          typed: '',
          completed: false,
        });
      } else if (r < 0.14) {
        result.push({
          target: HARD_SYMBOLS[Math.floor(Math.random() * HARD_SYMBOLS.length)],
          typed: '',
          completed: false,
        });
      }
    }
  }

  return result.slice(0, count);
}
